import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../api/apiClient';
import { formatItemsDisplay } from '../../utils/contractFormat';
import {
  cardStyle,
  cardTitleBarStyle,
  cardLabelStyle,
  emptyStyle,
  cancelledBadge,
  desktopInfoChip,
} from './contractmanagement/pageStyles';
import {
  stdPageTitle,
  stdPageSubtitle,
  stdHeaderRow,
  stdBlackBtn,
  stdOutlineBtn,
} from './adminlayout/designStandard';
import { DecisionInfo, DecisionStatusChip, getDecisionStatus } from './contractcancel/ContractCancelActions';

// 라벨 동적 생성 — 예전 PINE/DOT/LNJ 화이트리스트는 미등록 회사코드를 'PINE' 으로 되돌려
//   신규 회사(테넌트)의 계약취소 화면이 PINE 데이터를 요청하는 병목이었다.
//   코드 유효성 검증은 서버(isValidContractCancelCompany — external_companies 동적 검증)에 위임.
// 제목에 회사 접미사를 붙이지 않는다 (2026-09-14) — 상단 작업대상 선택창이 알려준다.
function labelForCompany() {
  return '계약취소 관리';
}

function normalizeCompanyCode(value) {
  const code = String(value || 'PINE').toUpperCase();
  return /^[A-Z0-9_]{2,20}$/.test(code) ? code : 'PINE';
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    function onResize() {
      setIsMobile(window.innerWidth <= 768);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

function toggleButtonStyle(active, disabled) {
  // 디자인 표준: 활성=검정 박스 / 비활성=흰 외곽선 (행 내부라 padding 만 compact 유지)
  return {
    ...(active ? stdBlackBtn : stdOutlineBtn),
    padding: '5px 10px',
    cursor: disabled ? 'wait' : 'pointer',
    opacity: disabled ? 0.65 : 1,
    whiteSpace: 'nowrap',
  };
}

function ContractCancelActions({ row, saving, onToggle }) {
  const status = getDecisionStatus(row);
  const included = status === 'included';
  const excluded = status === 'excluded';
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 800, whiteSpace: 'nowrap' }}>
        인센티브 반영
      </span>
      <DecisionStatusChip row={row} />
      <button
        type="button"
        style={toggleButtonStyle(included, saving)}
        disabled={saving || included}
        onClick={() => onToggle(row, true)}
      >
        포함 처리
      </button>
      <button
        type="button"
        style={toggleButtonStyle(excluded, saving)}
        disabled={saving || excluded}
        onClick={() => onToggle(row, false)}
      >
        제외 처리
      </button>
    </div>
  );
}

function CancelledContractRow({ row, isMobile, saving, onToggle }) {
  const items = Array.isArray(row.contract_items) ? row.contract_items : [];
  const contractDisplay = formatItemsDisplay(items);
  const serviceDisplay = formatItemsDisplay(items, { serviceOnly: true });
  const hasService = serviceDisplay !== '-';
  const contractDateText = row.contract_date || row.call_date || '-';
  const cancelledDateText = row.cancelled_date || '취소일 미확인';
  const paidQuantity = Number(row.paid_quantity || 0);

  if (isMobile) {
    return (
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid rgba(217,119,6,0.26)',
        borderLeft: '4px solid #d97706',
        borderRadius: 10,
        padding: '12px 14px',
        display: 'grid',
        gap: 8,
      }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={cancelledBadge}>계약취소</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 700 }}>취소일 {cancelledDateText}</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 700 }}>계약일 {contractDateText}</span>
          <strong style={{ fontSize: 15, color: 'var(--text)' }}>{row.company_name || '-'}</strong>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', color: 'var(--text-sub)', fontSize: 12 }}>
          {contractDisplay !== '-' && <span>{contractDisplay}</span>}
          {hasService && <span>서비스 {serviceDisplay}</span>}
          <span>{row.user_name || '-'}</span>
          <span>{paidQuantity.toLocaleString()}장</span>
          <DecisionInfo row={row} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <ContractCancelActions row={row} saving={saving} onToggle={onToggle} />
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid rgba(217,119,6,0.24)',
      borderLeft: '4px solid #d97706',
      borderRadius: 8,
      padding: '6px 10px',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      minWidth: 0,
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}>
        <span style={cancelledBadge}>계약취소</span>
        <span style={{ ...desktopInfoChip, fontWeight: 900, color: '#b45309' }}>취소일 {cancelledDateText}</span>
        <span style={desktopInfoChip}>계약일 {contractDateText}</span>
        <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 800, flexShrink: 0 }}>
          {row.company_name || '-'}
        </span>
        {contractDisplay !== '-' && (
          <span style={{ ...desktopInfoChip, color: '#166534', fontWeight: 700 }}>{contractDisplay}</span>
        )}
        {hasService && (
          <span style={{ ...desktopInfoChip, color: '#6d28d9', fontWeight: 700 }}>서비스 {serviceDisplay}</span>
        )}
        <span style={desktopInfoChip}>{row.user_name || '-'}</span>
        {row.sales_person_name && <span style={desktopInfoChip}>마케터 {row.sales_person_name}</span>}
        <span style={{ ...desktopInfoChip, fontWeight: 800 }}>{paidQuantity.toLocaleString()}장</span>
        <DecisionInfo row={row} />
        {row.memo && (
          <span
            title={row.memo}
            style={{
              ...desktopInfoChip,
              minWidth: 0,
              maxWidth: 150,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              background: 'rgba(234,179,8,0.12)',
              border: '1px solid rgba(234,179,8,0.3)',
              borderRadius: 6,
              padding: '1px 8px',
            }}
          >
            {row.memo}
          </span>
        )}
      </div>
      <ContractCancelActions row={row} saving={saving} onToggle={onToggle} />
    </div>
  );
}

export default function ContractCancelManagement({ companyCode = 'PINE' }) {
  const code = useMemo(() => normalizeCompanyCode(companyCode), [companyCode]);
  const title = labelForCompany(code);
  const isMobile = useIsMobile();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [message, setMessage] = useState('');

  const loadRows = useCallback(async () => {
    setLoading(true);
    setMessage('');
    const res = await apiClient.get(`/api/admin/contracts/cancelled?company_code=${encodeURIComponent(code)}`);
    setLoading(false);
    if (!res.ok) {
      setRows([]);
      setMeta({});
      setMessage(res.data?.message || '계약취소 목록을 불러오지 못했습니다.');
      return;
    }
    setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    setMeta(res.data?.meta || {});
  }, [code]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  async function handleToggle(row, include) {
    setSavingId(row.id);
    setMessage('');
    const res = await apiClient.patch(`/api/admin/contracts/${row.id}/cancelled-incentive`, {
      company_code: code,
      include,
    });
    setSavingId(null);
    if (!res.ok) {
      setMessage(res.data?.message || '저장에 실패했습니다.');
      return;
    }
    const nextRow = res.data?.data || {};
    setRows((prev) => prev.map((item) => (
      item.id === row.id
        ? { ...item, ...nextRow, incentive_include_cancelled: include }
        : item
    )));
    setMessage(include ? '인센티브 포함 처리했습니다.' : '인센티브 제외 처리했습니다.');
  }

  const appliedCount = rows.filter((row) => row.incentive_include_cancelled).length;
  const pendingCount = rows.filter((row) => getDecisionStatus(row) === 'pending').length;
  const excludedCount = rows.filter((row) => getDecisionStatus(row) === 'excluded').length;

  return (
    <div>
      <div style={stdHeaderRow(isMobile)}>
        <div>
          <h2 style={stdPageTitle}>{title}</h2>
          <div style={stdPageSubtitle}>계약 취소 요청·처리를 관리합니다.</div>
          <div style={stdPageSubtitle}>
            기준 카테고리 {meta.category_name || '-'} · 확인필요 {pendingCount.toLocaleString()}건 · 인센포함 {appliedCount.toLocaleString()}건 · 인센제외 {excludedCount.toLocaleString()}건 / 전체 {rows.length.toLocaleString()}건
          </div>
        </div>
        <button
          type="button"
          style={{ ...stdBlackBtn, opacity: loading ? 0.65 : 1 }}
          onClick={loadRows}
          disabled={loading}
        >
          {loading ? '새로고침 중...' : '새로고침'}
        </button>
      </div>

      {message && (
        <div style={{
          border: '1px solid var(--border)',
          background: 'var(--bg-card)',
          color: 'var(--text-sub)',
          borderRadius: 8,
          padding: '10px 12px',
          fontSize: 13,
          fontWeight: 700,
          marginBottom: 16,
        }}>
          {message}
        </div>
      )}

      <div style={{ ...cardStyle, borderRadius: 8, boxShadow: 'none' }}>
        <div style={cardTitleBarStyle}>
          <span style={cardLabelStyle}>취소 계약 목록</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 700 }}>
            {rows.length.toLocaleString()}건
          </span>
        </div>
        {loading
          ? <div style={emptyStyle}>불러오는 중...</div>
          : rows.length === 0
            ? <div style={emptyStyle}>조건에 맞는 취소 계약이 없습니다.</div>
            : (
              <div style={{ display: 'grid', gap: 8 }}>
                {rows.map((row) => (
                  <CancelledContractRow
                    key={row.id}
                    row={row}
                    isMobile={isMobile}
                    saving={savingId === row.id}
                    onToggle={handleToggle}
                  />
                ))}
              </div>
            )
        }
      </div>
    </div>
  );
}
